/**
 * Closing a period: is every card charge backed by a receipt, and is every card receipt on a
 * statement?
 *
 * Two sides, counted separately because they mean different work:
 *  - a charge without a receipt: a receipt still has to be found and uploaded;
 *  - a card receipt without a charge: the statement does not cover it yet, it was paid with a card
 *    the business does not track, or its payment method is wrong.
 * The period is complete when both are zero and no imported row is still waiting for a decision.
 */
import type { BankAccount, Transaction } from "@/lib/api/banking";
import type { DocumentRecord } from "@/lib/api/documents";
import { isWithin, type Period } from "@/lib/at/periods";
import { autoMatches } from "@/lib/banking/match";

/** The date a card charge carries on the receipt: the purchase, else the booking. */
export function transactionDate(transaction: Pick<Transaction, "purchaseDate" | "bookingDate">) {
  return (transaction.purchaseDate || transaction.bookingDate).slice(0, 10);
}

/** The date a card receipt was paid on. */
function paymentDate(document: Pick<DocumentRecord, "paidDate" | "issueDate">) {
  return (document.paidDate || document.issueDate || "").slice(0, 10);
}

export type AccountCheck = {
  account: BankAccount;
  /** Money out in the period. */
  charges: number;
  missing: Transaction[];
  missingTotal: number;
  /** Rows an import could not tell apart from another one. */
  review: number;
};

export type PeriodCheck = {
  accounts: AccountCheck[];
  missingCount: number;
  /** Card receipts in the period that no charge points to, up to the last imported charge. */
  unmatchedReceipts: DocumentRecord[];
  /** Card receipts dated after the last imported charge: the next statement will show them. */
  afterLastStatement: number;
  reviewCount: number;
  /** Nothing imported for the period yet, so there is nothing to check against. */
  noStatement: boolean;
  complete: boolean;
};

const round2 = (value: number) => Math.round(value * 100) / 100;

export function periodCheck(
  accounts: BankAccount[],
  transactions: Transaction[],
  documents: DocumentRecord[],
  period: Period,
): PeriodCheck {
  const cards = accounts.filter((account) => account.type === "credit_card" && !account.archived);
  const cardIds = new Set(cards.map((account) => account.id));
  const inPeriod = transactions.filter((transaction) =>
    isWithin(transactionDate(transaction), period),
  );

  const checks = cards.map((account) => {
    const own = inPeriod.filter((transaction) => transaction.account === account.id);
    const missing = own.filter(
      (transaction) =>
        transaction.amount < 0 &&
        transaction.status === "unmatched" &&
        !transaction.possibleDuplicateOf,
    );
    return {
      account,
      charges: own.filter((transaction) => transaction.amount < 0).length,
      missing,
      missingTotal: round2(missing.reduce((sum, row) => sum + Math.abs(row.amount), 0)),
      review: own.filter((transaction) => Boolean(transaction.possibleDuplicateOf)).length,
    };
  });

  const claimed = new Set(transactions.map((transaction) => transaction.document).filter(Boolean));
  const lastCharge = transactions
    .filter((transaction) => cardIds.has(transaction.account))
    .map(transactionDate)
    .sort()
    .at(-1);
  const open = documents.filter(
    (document) =>
      document.paymentMethod === "card" &&
      document.kind !== "AR" &&
      document.status !== "archived" &&
      !claimed.has(document.id) &&
      isWithin(paymentDate(document), period),
  );
  const unmatchedReceipts = open.filter(
    (document) => lastCharge !== undefined && paymentDate(document) <= lastCharge,
  );

  const missingCount = checks.reduce((sum, check) => sum + check.missing.length, 0);
  const reviewCount = checks.reduce((sum, check) => sum + check.review, 0);
  const noStatement = checks.every((check) => check.charges === 0 && check.review === 0);
  return {
    accounts: checks,
    missingCount,
    unmatchedReceipts,
    afterLastStatement: open.length - unmatchedReceipts.length,
    reviewCount,
    noStatement,
    complete:
      !noStatement && missingCount === 0 && unmatchedReceipts.length === 0 && reviewCount === 0,
  };
}

/**
 * The charges the matcher may link on its own: nobody decided on them yet (`matchedBy` empty),
 * they are not waiting for a duplicate decision, and the receipt is not linked elsewhere.
 */
export function autoLinkPlan(
  transactions: Transaction[],
  documents: DocumentRecord[],
): Array<{ transactionId: string; documentId: string }> {
  const claimed = new Set(transactions.map((transaction) => transaction.document).filter(Boolean));
  const open = transactions.filter(
    (transaction) =>
      transaction.status === "unmatched" &&
      !transaction.document &&
      !transaction.matchedBy &&
      !transaction.possibleDuplicateOf,
  );
  const free = documents.filter(
    (document) => document.status !== "archived" && !claimed.has(document.id),
  );
  return autoMatches(
    open.map((transaction) => ({
      id: transaction.id,
      bookingDate: transactionDate(transaction),
      amount: transaction.amount,
      counterparty: transaction.counterparty,
      description: transaction.description,
    })),
    free.map((document) => ({
      id: document.id,
      kind: document.kind,
      grossTotal: document.grossTotal,
      tip: document.tip,
      issueDate: document.issueDate,
      paidDate: document.paidDate,
      partnerName: document.partnerName,
      docNumber: document.docNumber,
    })),
  );
}
